import React, { useCallback, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Upload } from "lucide-react";

import { CGM_IN_RANGE } from "src/constants/cgm";

/** India Standard Time — axis labels and primary tooltip line. */
const TZ_IST = "Asia/Kolkata";

function formatDateTimeIST(ms: number): string {
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: TZ_IST,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(new Date(ms));
}

/** UTC for second tooltip line (Z). */
function formatDateTimeUTC(ms: number): string {
  return new Date(ms)
    .toISOString()
    .replace("T", " ")
    .replace(/\.\d{3}Z$/, " UTC (Z)");
}

function xAxisTickIST(ms: number): string {
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: TZ_IST,
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(ms));
}

type ChartDatum = { ts: number; glucose: number };

type CgmExportTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: unknown }>;
};

function CgmExportTooltip({ active, payload }: CgmExportTooltipProps) {
  if (!active || !payload?.length) return null;
  const raw = payload[0]?.payload;
  const row = typeof raw === "object" && raw !== null ? (raw as ChartDatum) : null;
  if (row == null || typeof row.ts !== "number") return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-medium text-slate-800 tabular-nums">
        {formatDateTimeIST(row.ts)} IST
      </div>
      <div className="mt-0.5 text-slate-500 font-mono tabular-nums text-[11px]">
        {formatDateTimeUTC(row.ts)}
      </div>
      <div className="mt-2 border-t border-slate-100 pt-2 text-slate-900">
        <span className="font-semibold">{row.glucose}</span>{" "}
        <span className="text-slate-500">mg/dL</span>
      </div>
    </div>
  );
}

/** Split one CSV row respecting double-quoted cells. */
function parseCSVLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && c === ",") {
      out.push(cur.trim());
      cur = "";
      continue;
    }
    cur += c;
  }
  out.push(cur.trim());
  return out;
}

export type ParsedPoint = {
  /** epoch ms — ZTIMEMILLES */
  timeMs: number;
  /** mg/dL */
  glucoseMgDl: number;
  receiveIso: string | null;
};

function headerIndex(headers: string[], key: string): number {
  const k = key.toUpperCase();
  return headers.findIndex((h) => h.replace(/^\ufeff/, "").toUpperCase() === k);
}

/**
 * Parses Tracky / Core Data–style CGM export CSV like
 * ZTEMPERATURE,...,ZTIMEMILLES,...,ZGLUCOSEMG,...,ZIW,ZRECEIVEDATETIME,...
 */
export function parseTrackyCgmCsv(text: string): {
  points: ParsedPoint[];
  error?: string;
} {
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter(Boolean);
  if (lines.length < 2)
    return { points: [], error: "CSV must contain a header and at least one row." };

  const headers = parseCSVLine(lines[0]!).map((h) =>
    h.replace(/^"|"$/g, "").replace(/^\ufeff/, "")
  );
  const ti = headerIndex(headers, "ZTIMEMILLES");
  const tg = headerIndex(headers, "ZGLUCOSEMG");
  const ziw = headerIndex(headers, "ZIW");
  const zrcv = headerIndex(headers, "ZRECEIVEDATETIME");

  if (ti < 0) return { points: [], error: "Missing ZTIMEMILLES column (time millis)." };

  const points: ParsedPoint[] = [];
  for (let r = 1; r < lines.length; r++) {
    const row = parseCSVLine(lines[r]!).map((c) =>
      String(c.replace(/^"|"$/g, ""))
    );
    const msRaw = row[ti];
    const ms = Number(msRaw);
    if (!Number.isFinite(ms) || ms <= 0) continue;

    let mg = tg >= 0 ? Number(row[tg]) : NaN;
    if (!Number.isFinite(mg) || mg < 20) {
      const iw = ziw >= 0 ? Number(row[ziw]) : NaN;
      if (Number.isFinite(iw) && iw > 0) mg = iw * 18.0182;
    }

    if (!Number.isFinite(mg) || mg < 15 || mg > 600) continue;

    points.push({
      timeMs: ms,
      glucoseMgDl: Math.round(mg * 10) / 10,
      receiveIso: zrcv >= 0 ? row[zrcv]?.trim() || null : null,
    });
  }

  points.sort((a, b) => a.timeMs - b.timeMs);
  const byMs = new Map<number, ParsedPoint>();
  for (const p of points) byMs.set(p.timeMs, p);

  return {
    points: Array.from(byMs.keys())
      .sort((a, b) => a - b)
      .map((k) => byMs.get(k)!),
  };
}

const CgmExportGraphDevPage: React.FC = () => {
  const [fileName, setFileName] = useState<string>("");
  const [parseErr, setParseErr] = useState<string>("");
  const [points, setPoints] = useState<ParsedPoint[]>([]);

  const chartData = useMemo<ChartDatum[]>(
    () =>
      points.map((p) => ({
        ts: p.timeMs,
        glucose: p.glucoseMgDl,
      })),
    [points]
  );

  const onFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setParseErr("");
    setPoints([]);
    setFileName("");
    e.target.value = "";
    if (!file) return;
    setFileName(file.name);
    file
      .text()
      .then((text) => {
        const res = parseTrackyCgmCsv(text);
        if (res.error) {
          setParseErr(res.error);
          return;
        }
        if (res.points.length === 0) {
          setParseErr("No usable glucose rows found (need ZTIMEMILLES + ZGLUCOSEMG or ZIW).");
          return;
        }
        setPoints(res.points);
      })
      .catch(() =>
        setParseErr("Could not read file.")
      );
  }, []);

  const range = CGM_IN_RANGE;
  const yDomain = useMemo<[number, number]>(() => {
    if (chartData.length === 0) return [40, 220];
    const vals = chartData.map((d) => d.glucose);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = 15;
    return [
      Math.max(40, Math.floor(min - pad)),
      Math.min(380, Math.ceil(max + pad)),
    ];
  }, [chartData]);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CGM export graph (dev)</h1>
          <p className="text-sm text-slate-600 mt-1">
            Upload a Tracky-style local DB CSV export.             Plots{" "}
            <code className="text-xs bg-slate-200 px-1 rounded">ZTIMEMILLES</code> vs{" "}
            <code className="text-xs bg-slate-200 px-1 rounded">ZGLUCOSEMG</code>{" "}
            (or <code className="text-xs bg-slate-200 px-1 rounded">ZIW</code> mmol/L × 18 when mg is 0).
            X-axis labels use <strong>IST</strong> (<code className="text-xs">Asia/Kolkata</code>); tooltip shows{" "}
            <strong>IST</strong> and <strong>UTC (Z)</strong>.
          </p>
        </div>

        <label className="flex flex-col items-center justify-center w-full border-2 border-dashed border-slate-300 rounded-xl bg-white p-10 cursor-pointer hover:border-indigo-400 transition-colors">
          <Upload className="w-10 h-10 text-slate-400 mb-2" />
          <span className="text-sm font-medium text-slate-700">Choose CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFile}
          />
        </label>

        {fileName && (
          <p className="text-sm text-slate-600">
            <span className="font-medium">File:</span> {fileName}
            {points.length > 0 && (
              <span className="ml-2 text-indigo-600">
                · {points.length} points
              </span>
            )}
          </p>
        )}

        {parseErr && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm">
            {parseErr}
          </div>
        )}

        {chartData.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-4">Glucose (mg/dL)</h2>
            <div className="h-[420px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 28 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="ts"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={(v) => xAxisTickIST(Number(v))}
                    stroke="#64748b"
                    fontSize={10}
                    interval="preserveStartEnd"
                    minTickGap={28}
                    tick={{ fill: "#64748b" }}
                    label={{
                      value: "Time (IST)",
                      position: "insideBottom",
                      offset: -4,
                      fill: "#64748b",
                      fontSize: 11,
                    }}
                  />
                  <YAxis
                    domain={yDomain}
                    stroke="#64748b"
                    fontSize={11}
                    width={44}
                  />
                  <Tooltip content={<CgmExportTooltip />} />
                  <ReferenceArea
                    y1={range.MIN}
                    y2={range.MAX}
                    fill="#22c55e"
                    fillOpacity={0.08}
                    strokeOpacity={0}
                  />
                  <Line
                    type="monotone"
                    dataKey="glucose"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Shaded band {range.MIN}–{range.MAX} mg/dL. X-axis tick labels are{" "}
              <span className="font-medium">IST</span> (sample time from{" "}
              <code className="text-[10px]">ZTIMEMILLES</code>).
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CgmExportGraphDevPage;
